// @givmo/board — the label a form from the library always carries.
//
// ══ WHAT THIS RENDERS, AND WHY EVERY WORD OF IT COMES FROM THE BACKEND ══════════════════════════
//
// The consent/resolution library is Band 2 under plan §3.2: a form ships only inside named controls,
// and one of those controls is that it is LABELLED FOR COUNSEL wherever it is shown. The backend
// carries that label as data on every form and on the officer's statement:
//
//   `review_line`      — the sentence itself, written once in the backend's own library and shared
//                        between the two form libraries so an organization sees one set of words.
//   `provenance`       — the authority the form's STRUCTURE is modeled on.
//   `source_citations` — that authority, cited so a reader can go and check it.
//
// All three are DISPLAYED, none are composed. This app writes no sentence of its own about what a form
// is or what an organization should do with it — that is the whole point of the rail, and a UI that "helpfully"
// rephrased the review line into product voice would step over the Band-2 line the backend data exists
// to hold.
//
// ══ ONE SMALL LINE (CBG-FIX40) ══════════════════════════════════════════════════════════════════
//
// The label is as small and subordinate as the rail allows (Jonathan's standing ruling on disclaimer
// copy): the review line and the provenance, run together as ONE small line in the quiet tone under
// the form's name — no panel — as the conflict-of-interest questionnaire's label is drawn (the CBG-YR2
// mockups, frame `o2b`). The sources sit behind a "Sources" link that closes the line, each citation
// its own line in Board's fine-print popover: a consent form's three run about thirteen lines on a
// phone, and the review line stays in view (copy round CBG-YR3-ROLES-COPY1, Jonathan, 2026-10-12
// 13:49 PDT, the page's recommendation). "Sources" is the one word this app adds.
//
// ⚠ THE REGISTERED CLASS IDS ARE NEVER SHOWN. The backend serves `disclaimer_class_ids` beside the
// label: references into the disclaimer registry (`template-review-with-counsel`, …), not words for a
// reader. This notice once printed them ("Shown under: …"), which put an internal id in front of a
// volunteer (CBG-FIX40). The words that class stands for are the review line above.
//
// ⚠ AND NOTE WHAT THE SANCTIONED WORDING IS. §3.4's affirmative line is "template — review with your
// counsel before adopting"; the DENIAL ("this is not legal advice") is banned, because a surface that
// says it has raised the frame it is disclaiming. The backend's line is written to that rule, which is
// another reason it is rendered verbatim rather than adapted.
//
// ══ THE COUNSEL SENTENCE, ON ITS OWN LINE (CBG-ST2-EFFECTIVE, D-CBG-62) ════════════════════════════
//
// Under the one small line sits Board's counsel sentence: the `not-your-lawyer` class the backend
// names beside every form, which Jonathan approved on 2026-11-03 (D-CBG-60) and the disclaimer
// registry serves at its version 2. Unlike the review line, which is the backend library's data, it is
// a registry document, so `CounselSentence` resolves it and draws nothing until it is in force. It is
// drawn at Compliance's size on a line of its own, never run into the review line, because it is the
// statement Texas's software exclusion asks to be made clearly and conspicuously.

import { testIdProp } from '@givmo/design-system';
import { HowThisWorks } from '../../components/fine-print';
import { CounselSentence } from '../../components/registered-disclaimer';

export interface TemplateNoticeProps {
  /** The organization the form is opened for: its counsel sentence is resolved for it. */
  orgRef: string;
  /** The backend's own review line. Rendered character for character. */
  reviewLine: string;
  /** The authority the form's structure is modeled on. */
  provenance?: string | null;
  sourceCitations?: readonly string[] | null;
  testId?: string;
}

export function TemplateNotice({
  orgRef,
  reviewLine,
  provenance,
  sourceCitations,
  testId = 'consent-template-notice',
}: TemplateNoticeProps) {
  const citations = (sourceCitations ?? []).filter((citation) => citation.trim().length > 0);

  return (
    <div className="flex flex-col gap-8">
      <p
        className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary"
        {...testIdProp(testId)}
      >
        <span data-testid={`${testId}-review-line`}>{reviewLine}</span>
        {provenance ? (
          <>
            {' '}
            <span data-testid={`${testId}-provenance`}>{provenance}</span>
          </>
        ) : null}
        {citations.length > 0 ? (
          <>
            {' '}
            <HowThisWorks
              label="Sources"
              lines={citations}
              testId={`${testId}-sources`}
              className="text-s12"
            />
          </>
        ) : null}
      </p>
      <CounselSentence orgRef={orgRef} testId={`${testId}-counsel`} />
    </div>
  );
}
